import { useState, useEffect } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { delftBlue } from "@/theme/colors";
import ContactButtonConfig from "./components/ContactButtonConfig";
import {
  useGetEmergencyConfigQuery,
  useUpdateCompanyEmergencyNumbersMutation,
  useUpdateGlobalEmergencyNumberMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";
import mobileImage from "@/assets/mobile.png";
import { validateWorldwideContactNumber } from "@/utils/worldwidePhone";

const ContactsPage = () => {
  const user = useSelector(selectUser);
  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN;
  const isAdmin = user?.role === Roles.ADMIN;
  const isGuide = user?.role === Roles.GUIDE;

  // Fetch emergency config data
  const { data: emergencyConfigData, isLoading, refetch } = useGetEmergencyConfigQuery();
  const [updateCompanyNumbers, { isLoading: isUpdatingCompany }] = useUpdateCompanyEmergencyNumbersMutation();
  const [updateGlobalEmergency, { isLoading: isUpdatingGlobal }] = useUpdateGlobalEmergencyNumberMutation();

  // Track which button is currently being saved
  const [savingButton, setSavingButton] = useState(null);

  // Error state for API errors
  const [errorMessage, setErrorMessage] = useState(null);

  // Success messages for each button
  const [successMessages, setSuccessMessages] = useState({
    button1: null,
    button2: null,
    button3: null,
    emergency: null,
  });

  // Validation errors for each button
  const [validationErrors, setValidationErrors] = useState({
    button1: null,
    button2: null,
    button3: null,
    emergency: null,
  });

  const validatePhoneNumber = validateWorldwideContactNumber;

  // State for button configurations
  const [button1, setButton1] = useState({
    disabled: false,
    buttonName: "",
    contactNumber: "",
    originalData: null,
  });

  const [button2, setButton2] = useState({
    disabled: false,
    buttonName: "",
    contactNumber: "",
    originalData: null,
  });

  const [button3, setButton3] = useState({
    disabled: false,
    buttonName: "",
    contactNumber: "",
    originalData: null,
  });

  const [emergencyButton, setEmergencyButton] = useState({
    disabled: false,
    buttonName: "",
    contactNumber: "",
    originalData: null,
  });

  // Initialize state from API data
  useEffect(() => {
    if (emergencyConfigData?.data) {
      const { companyNumbers = [], globalEmergency } = emergencyConfigData.data;

      // Set first 3 buttons from company numbers
      const buttons = [
        companyNumbers[0] || { name: "", phoneNumber: "", isActive: true },
        companyNumbers[1] || { name: "", phoneNumber: "", isActive: true },
        companyNumbers[2] || { name: "", phoneNumber: "", isActive: true },
      ];

      // Preserve current state when refetching after save
      // Only update state on initial load (when originalData is null)
      setButton1((prev) => {
        const isInitialLoad = prev.originalData === null;
        return {
          disabled: isInitialLoad ? !buttons[0].isActive : prev.disabled,
          buttonName: isInitialLoad ? (buttons[0].name || "") : prev.buttonName,
          contactNumber: isInitialLoad ? (buttons[0].phoneNumber || "") : prev.contactNumber,
          originalData: buttons[0],
        };
      });

      setButton2((prev) => {
        const isInitialLoad = prev.originalData === null;
        return {
          disabled: isInitialLoad ? !buttons[1].isActive : prev.disabled,
          buttonName: isInitialLoad ? (buttons[1].name || "") : prev.buttonName,
          contactNumber: isInitialLoad ? (buttons[1].phoneNumber || "") : prev.contactNumber,
          originalData: buttons[1],
        };
      });

      setButton3((prev) => {
        const isInitialLoad = prev.originalData === null;
        return {
          disabled: isInitialLoad ? !buttons[2].isActive : prev.disabled,
          buttonName: isInitialLoad ? (buttons[2].name || "") : prev.buttonName,
          contactNumber: isInitialLoad ? (buttons[2].phoneNumber || "") : prev.contactNumber,
          originalData: buttons[2],
        };
      });

      // Set emergency button from global emergency
      if (globalEmergency) {
        setEmergencyButton((prev) => {
          const isInitialLoad = prev.originalData === null;
          // For super admin, always keep emergency button enabled (disabled = false)
          // For other roles, use the isActive status from API
          const shouldBeDisabled = isSuperAdmin ? false : (isInitialLoad ? !globalEmergency.isActive : prev.disabled);
          return {
            disabled: shouldBeDisabled,
            buttonName: isInitialLoad ? (globalEmergency.name || "") : prev.buttonName,
            contactNumber: isInitialLoad ? (globalEmergency.phoneNumber || "") : prev.contactNumber,
            originalData: globalEmergency,
          };
        });
      } else {
        // Default to activated state if no global emergency exists
        setEmergencyButton((prev) => {
          const isInitialLoad = prev.originalData === null;
          // For super admin, always keep emergency button enabled
          const shouldBeDisabled = isSuperAdmin ? false : (isInitialLoad ? false : prev.disabled);
          return {
            disabled: shouldBeDisabled,
            buttonName: isInitialLoad ? "" : prev.buttonName,
            contactNumber: isInitialLoad ? "" : prev.contactNumber,
            originalData: null,
          };
        });
      }
    }
  }, [emergencyConfigData]);

  // Helper function to check if a card should be disabled (enabled but missing name/number)
  const shouldDisableCard = (button) => {
    // If disabled is false (enabled), check if name or number is missing
    if (!button.disabled) {
      const hasName = button.buttonName && button.buttonName.trim() !== "";
      const hasNumber = button.contactNumber && button.contactNumber.trim() !== "";
      // If enabled but missing name or number (or both), it should be disabled
      return !hasName || !hasNumber;
    }
    return false;
  };

  const handleToggle = async (buttonType) => {
    // For super admin, prevent toggling emergency button (it should always be enabled)
    if (buttonType === "emergency" && isSuperAdmin) {
      return;
    }

    // Clear success message when user makes changes
    setSuccessMessages((prev) => ({ ...prev, [buttonType]: null }));

    // Update the toggle state
    let updatedButton1 = button1;
    let updatedButton2 = button2;
    let updatedButton3 = button3;
    let updatedEmergency = emergencyButton;

    if (buttonType === "button1") {
      updatedButton1 = { ...button1, disabled: !button1.disabled };
      setButton1(updatedButton1);
    } else if (buttonType === "button2") {
      updatedButton2 = { ...button2, disabled: !button2.disabled };
      setButton2(updatedButton2);
    } else if (buttonType === "button3") {
      updatedButton3 = { ...button3, disabled: !button3.disabled };
      setButton3(updatedButton3);
    } else if (buttonType === "emergency") {
      updatedEmergency = { ...emergencyButton, disabled: !emergencyButton.disabled };
      setEmergencyButton(updatedEmergency);
    }

    // Immediately call the API after toggle change
    // The handleSaveWithState function will validate and auto-disable any cards
    // that are enabled but missing name/number before submitting
    setTimeout(() => {
      handleSaveWithState(buttonType, updatedButton1, updatedButton2, updatedButton3, updatedEmergency);
    }, 0);
  };

  const handleButtonNameChange = (buttonType, value) => {
    // Clear success message when user starts editing
    setSuccessMessages((prev) => ({ ...prev, [buttonType]: null }));

    if (buttonType === "button1") {
      setButton1((prev) => ({ ...prev, buttonName: value }));
    } else if (buttonType === "button2") {
      setButton2((prev) => ({ ...prev, buttonName: value }));
    } else if (buttonType === "button3") {
      setButton3((prev) => ({ ...prev, buttonName: value }));
    } else if (buttonType === "emergency") {
      setEmergencyButton((prev) => ({ ...prev, buttonName: value }));
    }
  };

  const handleContactNumberChange = (buttonType, value) => {
    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage(null);
    }

    // Clear validation error for this button
    setValidationErrors((prev) => ({ ...prev, [buttonType]: null }));

    // Clear success message when user starts editing
    setSuccessMessages((prev) => ({ ...prev, [buttonType]: null }));

    // Allow international formatting characters while typing
    const sanitizedValue = value.replace(/[^\d+\s\-()]/g, "");

    if (buttonType === "button1") {
      setButton1((prev) => ({ ...prev, contactNumber: sanitizedValue }));
    } else if (buttonType === "button2") {
      setButton2((prev) => ({ ...prev, contactNumber: sanitizedValue }));
    } else if (buttonType === "button3") {
      setButton3((prev) => ({ ...prev, contactNumber: sanitizedValue }));
    } else if (buttonType === "emergency") {
      setEmergencyButton((prev) => ({ ...prev, contactNumber: sanitizedValue }));
    }
  };

  const handleCancel = (buttonType) => {
    // Reset to original values from API
    if (buttonType === "button1" && button1.originalData) {
      setButton1({
        disabled: !button1.originalData.isActive,
        buttonName: button1.originalData.name || "",
        contactNumber: button1.originalData.phoneNumber || "",
        originalData: button1.originalData,
      });
    } else if (buttonType === "button2" && button2.originalData) {
      setButton2({
        disabled: !button2.originalData.isActive,
        buttonName: button2.originalData.name || "",
        contactNumber: button2.originalData.phoneNumber || "",
        originalData: button2.originalData,
      });
    } else if (buttonType === "button3" && button3.originalData) {
      setButton3({
        disabled: !button3.originalData.isActive,
        buttonName: button3.originalData.name || "",
        contactNumber: button3.originalData.phoneNumber || "",
        originalData: button3.originalData,
      });
    } else if (buttonType === "emergency" && emergencyButton.originalData) {
      // For super admin, always keep emergency enabled
      const shouldBeDisabled = isSuperAdmin ? false : !emergencyButton.originalData.isActive;
      setEmergencyButton({
        disabled: shouldBeDisabled,
        buttonName: emergencyButton.originalData.name || "",
        contactNumber: emergencyButton.originalData.phoneNumber || "",
        originalData: emergencyButton.originalData,
      });
    }
  };

  // Helper function to validate and auto-disable cards before saving
  const validateAndAutoDisableCards = (btn1, btn2, btn3, emergency) => {
    let updatedButton1 = { ...btn1 };
    let updatedButton2 = { ...btn2 };
    let updatedButton3 = { ...btn3 };
    let updatedEmergency = { ...emergency };
    let hasChanges = false;

    // Check and auto-disable company buttons
    if (shouldDisableCard(updatedButton1)) {
      // For guide role, clear details when auto-disabling
      // For admin/super admin, keep the details
      if (isGuide) {
        updatedButton1 = { ...updatedButton1, disabled: true, buttonName: "", contactNumber: "" };
      } else {
        updatedButton1 = { ...updatedButton1, disabled: true };
      }
      hasChanges = true;
    }
    if (shouldDisableCard(updatedButton2)) {
      // For guide role, clear details when auto-disabling
      // For admin/super admin, keep the details
      if (isGuide) {
        updatedButton2 = { ...updatedButton2, disabled: true, buttonName: "", contactNumber: "" };
      } else {
        updatedButton2 = { ...updatedButton2, disabled: true };
      }
      hasChanges = true;
    }
    if (shouldDisableCard(updatedButton3)) {
      // For guide role, clear details when auto-disabling
      // For admin/super admin, keep the details
      if (isGuide) {
        updatedButton3 = { ...updatedButton3, disabled: true, buttonName: "", contactNumber: "" };
      } else {
        updatedButton3 = { ...updatedButton3, disabled: true };
      }
      hasChanges = true;
    }
    // For super admin, emergency button should always be enabled, so skip validation
    if (shouldDisableCard(updatedEmergency) && !isSuperAdmin) {
      // For guide role, clear details when auto-disabling
      // For admin, keep the details
      if (isGuide) {
        updatedEmergency = { ...updatedEmergency, disabled: true, buttonName: "", contactNumber: "" };
      } else {
        updatedEmergency = { ...updatedEmergency, disabled: true };
      }
      hasChanges = true;
    }
    // For super admin, ensure emergency is always enabled
    if (isSuperAdmin && updatedEmergency.disabled) {
      updatedEmergency = { ...updatedEmergency, disabled: false };
      hasChanges = true;
    }

    if (hasChanges) {
      setButton1(updatedButton1);
      setButton2(updatedButton2);
      setButton3(updatedButton3);
      setEmergencyButton(updatedEmergency);
    }

    return { updatedButton1, updatedButton2, updatedButton3, updatedEmergency };
  };

  const handleSaveWithState = async (
    buttonType,
    btn1 = button1,
    btn2 = button2,
    btn3 = button3,
    emergency = emergencyButton
  ) => {
    try {
      setErrorMessage(null);
      setSavingButton(buttonType);

      // Validate and auto-disable cards that are enabled but missing name/number
      const { updatedButton1, updatedButton2, updatedButton3, updatedEmergency } =
        validateAndAutoDisableCards(btn1, btn2, btn3, emergency);

      if (buttonType === "emergency") {
        // Save global emergency (Super Admin only)
        if (!isSuperAdmin) {
          console.error("Only Super Admin can update global emergency");
          setSavingButton(null);
          return;
        }

        // Validate emergency button phone number
        const emergencyValidation = validatePhoneNumber(updatedEmergency.contactNumber);
        if (emergencyValidation) {
          setValidationErrors((prev) => ({ ...prev, emergency: emergencyValidation }));
          setErrorMessage(emergencyValidation);
          setSavingButton(null);
          return;
        }

        await updateGlobalEmergency({
          globalEmergencyName: updatedEmergency.buttonName,
          globalEmergencyNumber: updatedEmergency.contactNumber,
        }).unwrap();
        await refetch();
        // Clear validation errors on successful save
        setValidationErrors({
          button1: null,
          button2: null,
          button3: null,
          emergency: null,
        });
        // Set success message for emergency button
        setSuccessMessages((prev) => ({ ...prev, emergency: "Details updated successfully!" }));
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessages((prev) => ({ ...prev, emergency: null }));
        }, 5000);
      } else {
        // Save company emergency numbers - always send all 3 buttons
        // Validate all phone numbers before saving
        const newValidationErrors = {
          button1: null,
          button2: null,
          button3: null,
        };
        const errorMessages = [];

        [updatedButton1, updatedButton2, updatedButton3].forEach((btn, index) => {
          const buttonKey = `button${index + 1}`;
          const validation = validatePhoneNumber(btn.contactNumber);
          if (validation) {
            newValidationErrors[buttonKey] = validation;
            errorMessages.push(`Button ${index + 1}: ${validation}`);
          }
        });

        if (errorMessages.length > 0) {
          setValidationErrors((prev) => ({ ...prev, ...newValidationErrors }));
          setErrorMessage(errorMessages.join("; "));
          setSavingButton(null);
          return;
        }

        const companyNumbers = [];

        // Collect all three company buttons in order
        [updatedButton1, updatedButton2, updatedButton3].forEach((btn) => {
          companyNumbers.push({
            name: btn.buttonName || "",
            phoneNumber: btn.contactNumber || "",
            isActive: !btn.disabled,
          });
        });

        await updateCompanyNumbers({
          companyNumbers: companyNumbers,
        }).unwrap();
        await refetch();
        // Clear validation errors on successful save
        setValidationErrors({
          button1: null,
          button2: null,
          button3: null,
          emergency: null,
        });
        // Set success message for the button that triggered the save
        // Since all buttons are saved together, show success for the triggering button
        setSuccessMessages((prev) => ({ ...prev, [buttonType]: "Details updated successfully!" }));
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessages((prev) => ({ ...prev, [buttonType]: null }));
        }, 5000);
      }
    } catch (error) {
      console.error("Error saving emergency config:", error);
      // Extract error message from API response
      const errorMsg =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "Failed to save contact information. Please try again.";
      setErrorMessage(errorMsg);
      // Clear validation errors on API error (API error takes precedence)
      setValidationErrors({
        button1: null,
        button2: null,
        button3: null,
        emergency: null,
      });
    } finally {
      setSavingButton(null);
    }
  };

  const handleSave = async (buttonType) => {
    // Use current state for manual save button clicks
    await handleSaveWithState(buttonType);
  };

  if (isLoading) {
    return (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          alignSelf: "stretch",
          minHeight: "100vh",
          paddingTop: { xs: 2, sm: 2.5, md: 3 },
          paddingLeft: { xs: 1.5, sm: 2, md: 3 },
          paddingRight: { xs: 1.5, sm: 2, md: 3 },
          paddingBottom: { xs: 2, sm: 2.5, md: 3 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography sx={{ color: delftBlue[500] }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        {/* Error Alert */}
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage(null)}
            sx={{
              alignSelf: "stretch",
              borderRadius: 2,
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Contacts
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Manage contact button configurations
            </Typography>
          </Box>
        </Box>

        {/* Button Configuration Sections */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {!isSuperAdmin ? (
            <>
              {/* Admin Layout - Grid with equal width cards aligned across rows */}
              <Box
                sx={{
                  alignSelf: "stretch",
                  display: { xs: "flex", md: "grid" },
                  flexDirection: { xs: "column", md: "unset" },
                  gridTemplateColumns: {
                    md: "1fr 1fr auto",
                    lg: "1fr 1fr auto"
                  },
                  gap: { xs: 2, sm: 2.5, md: 3 },
                  alignItems: "flex-start",
                }}
              >
                {/* First Row - Card 1 */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "100%" },
                    gridColumn: { md: "1 / 2" },
                    gridRow: { md: "1 / 2" },
                  }}
                >
                  <ContactButtonConfig
                    title="Disable this button"
                    buttonLabel="First Button Name"
                    description="This name will appear as the call button label and can be edited by the companies you add."
                    buttonName={button1.buttonName}
                    contactNumber={button1.contactNumber}
                    disabled={button1.disabled}
                    onToggle={() => handleToggle("button1")}
                    onButtonNameChange={(value) => handleButtonNameChange("button1", value)}
                    onContactNumberChange={(value) => handleContactNumberChange("button1", value)}
                    onCancel={() => handleCancel("button1")}
                    onSave={() => handleSave("button1")}
                    isSaving={savingButton === "button1"}
                    contactNumberError={validationErrors.button1 || validatePhoneNumber(button1.contactNumber)}
                    successMessage={successMessages.button1}
                    isSuperAdmin={isSuperAdmin}
                  />
                </Box>

                {/* First Row - Card 2 */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "100%" },
                    gridColumn: { md: "2 / 3" },
                    gridRow: { md: "1 / 2" },
                  }}
                >
                  <ContactButtonConfig
                    title="Disable this button"
                    buttonLabel="Second Button Name"
                    description="This name will appear as the call button label and can be edited by the companies you add."
                    buttonName={button2.buttonName}
                    contactNumber={button2.contactNumber}
                    disabled={button2.disabled}
                    onToggle={() => handleToggle("button2")}
                    onButtonNameChange={(value) => handleButtonNameChange("button2", value)}
                    onContactNumberChange={(value) => handleContactNumberChange("button2", value)}
                    onCancel={() => handleCancel("button2")}
                    onSave={() => handleSave("button2")}
                    isSaving={savingButton === "button2"}
                    contactNumberError={validationErrors.button2 || validatePhoneNumber(button2.contactNumber)}
                    successMessage={successMessages.button2}
                    isSuperAdmin={isSuperAdmin}
                  />
                </Box>

                {/* First Row - Mobile Image */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    gridColumn: { md: "3 / 4" },
                    gridRow: { md: "1 / 3" },
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-start" },
                    alignItems: "flex-start",
                    alignSelf: { md: "stretch" },
                  }}
                >
                  <Box
                    component="img"
                    src={mobileImage}
                    alt="Mobile"
                    sx={{
                      width: { xs: "100%", sm: "auto", md: "auto" },
                      maxWidth: { xs: "100%", sm: 300, md: 400 },
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                {/* Second Row - Card 3 */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "100%" },
                    gridColumn: { md: "1 / 2" },
                    gridRow: { md: "2 / 3" },
                  }}
                >
                  <ContactButtonConfig
                    title="Disable this button"
                    buttonLabel="Third Button Name"
                    description="This name will appear as the call button label and can be edited by the companies you add."
                    buttonName={button3.buttonName}
                    contactNumber={button3.contactNumber}
                    disabled={button3.disabled}
                    onToggle={() => handleToggle("button3")}
                    onButtonNameChange={(value) => handleButtonNameChange("button3", value)}
                    onContactNumberChange={(value) => handleContactNumberChange("button3", value)}
                    onCancel={() => handleCancel("button3")}
                    onSave={() => handleSave("button3")}
                    isSaving={savingButton === "button3"}
                    contactNumberError={validationErrors.button3 || validatePhoneNumber(button3.contactNumber)}
                    successMessage={successMessages.button3}
                    isSuperAdmin={isSuperAdmin}
                  />
                </Box>

                {/* Second Row - Emergency Button */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "100%" },
                    gridColumn: { md: "2 / 3" },
                    gridRow: { md: "2 / 3" },
                  }}
                >
                  <ContactButtonConfig
                    title="Disable this button"
                    buttonLabel="Emergency Button Name"
                    description="This name will appear as the call button label and can not be edited by the companies you add."
                    buttonName={emergencyButton.buttonName}
                    contactNumber={emergencyButton.contactNumber}
                    disabled={emergencyButton.disabled}
                    isEmergency={true}
                    onToggle={undefined}
                    onButtonNameChange={undefined}
                    onContactNumberChange={undefined}
                    onCancel={undefined}
                    onSave={undefined}
                    hideButtons={true}
                  />
                </Box>
              </Box>
            </>
          ) : (
            /* Super Admin - Emergency Button and Mobile Image side by side */
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: { xs: 2, sm: 2.5, md: 3 },
                flexDirection: { xs: "column", md: "row" },
                flexWrap: { xs: "nowrap", md: "nowrap" },
              }}
            >
              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 0" },
                  minWidth: 0,
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <ContactButtonConfig
                  title="Disable this button"
                  buttonLabel="Emergency Button Name"
                  description="This name will appear as the call button label and can not be edited by the companies you add."
                  buttonName={emergencyButton.buttonName}
                  contactNumber={emergencyButton.contactNumber}
                  disabled={false}
                  isEmergency={true}
                  onToggle={undefined}
                  hideToggle={true}
                  onButtonNameChange={(value) => handleButtonNameChange("emergency", value)}
                  onContactNumberChange={(value) => handleContactNumberChange("emergency", value)}
                  onCancel={() => handleCancel("emergency")}
                  onSave={() => handleSave("emergency")}
                  isSaving={savingButton === "emergency"}
                  contactNumberError={validationErrors.emergency || validatePhoneNumber(emergencyButton.contactNumber)}
                  successMessage={successMessages.emergency}
                  isSuperAdmin={isSuperAdmin}
                />
              </Box>

              {/* Mobile Image */}
              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "0 0 auto" },
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                  alignItems: "flex-start",
                }}
              >
                <Box
                  component="img"
                  src={mobileImage}
                  alt="Mobile"
                  sx={{
                    width: { xs: "100%", sm: "auto", md: "auto" },
                    maxWidth: { xs: "100%", sm: 300, md: 400 },
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default ContactsPage;

